import React, {ReactElement, useEffect, useMemo, useRef, useState} from "react";
import Draft, {
    ContentState,
    convertFromRaw,
    convertToRaw,
    EditorState, RawDraftContentState,
    RawDraftEntityRange,
} from "draft-js";
import Editor from "@draft-js-plugins/editor";
import createMentionPlugin, {MentionData} from "@draft-js-plugins/mention";
import {EntryComponentProps} from "@draft-js-plugins/mention/lib/MentionSuggestions/Entry/Entry";
import {differ, getSmsMessageLength} from "../../util/util";
import {EditorEmptyRawState} from "./templateEditorDefaultState";
import {isDebug} from "../../appConfig";

function Entry(props: EntryComponentProps): ReactElement {
    const {mention, ...parentProps} = props
    const {name, type} = mention
    return (
        <div {...parentProps}>
            <div className={"flex flex-row items-center justify-between space-x-4 px-2 py-2"}>
                <p className={"text-xs text-gray-1 font-medium"}>{name}</p>
                <p className={"text-xs text-blue-1"}>{type}</p>
            </div>
        </div>
    )
}

export enum MentionType {
    link = "Link",
    name = "Name",
    phone = "Phone",
    date = "Date",
    survey = "Survey",
}

export enum MentionKey {
    // "https://mobile.mdland.com/t/?oa=1"
    appDownloadLink = "{APP_DOWNLOAD_URL}",
    appointmentLink = "{DOCTOR_PUBLIC_URL}",
    appointmentConfirmationLink = "{APPOINTMENT_URL}",
    appointmentTime = "{APPOINTMENT_TIME}",
    providerName = "{PROVIDER_NAME}",
    patientName = "{PATIENT_NAME}",
    patientFirstName = "{PATIENT_FIRST_NAME}",
    practiceName = "{PRACTICE_NAME}",
    officePhone = "{OFFICE_PHONE_NUMBER}"
}

const mentions: Array<MentionData> = [
    {
        name: "iClinicHealth App Download Link",
        link: MentionKey.appDownloadLink,
        type: MentionType.link,
    },
    {
        name: "Appointment Booking Link",
        link: MentionKey.appointmentLink,
        type: MentionType.link,
    },
    {
        name: "Appointment Confirmation Link",
        link: MentionKey.appointmentConfirmationLink,
        type: MentionType.link,
    },
    {
        name: "Appointment Time",
        link: MentionKey.appointmentTime,
        type: MentionType.date,
    },
    {
        name: "Provider Name",
        link: MentionKey.providerName,
        type: MentionType.name,
    },
    {
        name: "Patient Name",
        link: MentionKey.patientName,
        type: MentionType.name,
    },
    {
        name: "Patient First Name",
        link: MentionKey.patientFirstName,
        type: MentionType.name,
    },
    {
        name: "Practice Name",
        link: MentionKey.practiceName,
        type: MentionType.name,
    },
    {
        name: "Office Phone Number",
        link: MentionKey.officePhone,
        type: MentionType.phone,
    },
];

export interface ReplaceableInfo {
    doctorPublicUrl?: string,
    appointmentUrl?: string,
    appointmentTime?: string,
    providerName?: string,
    patientName?: string,
    patientFirstName?: string,
    practiceName?: string,
    officePhone?: string
}

interface IProps {
    readonly?: boolean,
    libMessage: string,
    maxlength: number,
    availableMentionKeys?: Array<MentionKey>,
    replaceableInfo?: ReplaceableInfo,
    onEditorFocus?: () => void,
    onEditorBlur?: (libMessage: string, compliedMessage: string) => void,
    onEditorChange?: (libMessage: string, compliedMessage: string) => void
}

export function replaceWithReplaceableInfo(message: string, replaceableInfo?: ReplaceableInfo): string {
    message.replaceAll(MentionKey.appDownloadLink, `https://static-app.iclinichealth.${isDebug ? "dev" : "com"}/#/oa?src=1`)

    if (!replaceableInfo) {
        return message
    }

    message = replaceableInfo.doctorPublicUrl ? message.replaceAll(MentionKey.appointmentLink, replaceableInfo.doctorPublicUrl) : message
    message = replaceableInfo.appointmentUrl ? message.replaceAll(MentionKey.appointmentConfirmationLink, replaceableInfo.appointmentUrl) : message
    message = replaceableInfo.appointmentTime ? message.replaceAll(MentionKey.appointmentTime, replaceableInfo.appointmentTime) : message
    message = replaceableInfo.providerName ? message.replaceAll(MentionKey.providerName, replaceableInfo.providerName) : message
    message = replaceableInfo.patientName ? message.replaceAll(MentionKey.patientName, replaceableInfo.patientName) : message
    message = replaceableInfo.patientFirstName ? message.replaceAll(MentionKey.patientFirstName, replaceableInfo.patientFirstName) : message
    message = replaceableInfo.practiceName ? message.replaceAll(MentionKey.practiceName, replaceableInfo.practiceName) : message
    message = replaceableInfo.officePhone ? message.replaceAll(MentionKey.officePhone, replaceableInfo.officePhone) : message

    return message
}

export function getCompliedMessageFromLibMessage(libMessage: string): string {
    if (libMessage) {
        const rawState: RawDraftContentState = JSON.parse(libMessage)
        return getCompliedMessageFromRawState(rawState)
    }
    return ""
}

function getCompliedMessageFromRawState(rawState: RawDraftContentState): string {
    const {blocks, entityMap} = rawState
    let message = ""
    // todo --- Use native api to improve here.
    blocks.forEach((block) => {
        const {text, entityRanges} = block
        message += text
        entityRanges.forEach(({key}: RawDraftEntityRange) => {
            const link = entityMap[key].data.mention.link
            const name = "#" + entityMap[key].data.mention.name
            message = message.replace(name, link)
        })
    })
    return message
}

export const defaultAvailableMentionKeys = [
    // MentionKey.appDownloadLink,
    MentionKey.appointmentLink,
    // MentionKey.patientFirstName,
    MentionKey.appointmentConfirmationLink,
    MentionKey.appointmentTime,
    MentionKey.providerName,
    MentionKey.patientName,
    MentionKey.practiceName,
    MentionKey.officePhone,
]

export default function TemplateEditor(props: IProps) {
    const {readonly = false, libMessage = EditorEmptyRawState, maxlength, availableMentionKeys = defaultAvailableMentionKeys, replaceableInfo, onEditorFocus, onEditorBlur, onEditorChange} = props
    const ref = useRef<Editor | null>(null);

    const [editorState, setEditorState] = useState(() =>
        EditorState.createEmpty()
    );
    const [open, setOpen] = useState(false);

    const originalMentions = mentions.filter((item) => availableMentionKeys.find((key) => key === item.link) !== undefined)
    const [suggestions, setSuggestions] = useState(originalMentions);

    // useEffect(() => {
    //     (libMessage === EditorEmptyRawState) && onFocus()
    // }, [])

    useEffect(() => {
        setSuggestions(originalMentions)
    }, [availableMentionKeys])

    useEffect(() => {
        const editorTimer = updateEditor()
        return () => {
            editorTimer && clearTimeout(editorTimer)
        }
    }, [libMessage])

    const updateEditor = () => {
        const rawState = JSON.parse(libMessage);
        const contentState = convertFromRaw(rawState);
        const newEditorState = EditorState.createWithContent(contentState);
        const oldText = editorState.getCurrentContent().getPlainText();
        const text = newEditorState.getCurrentContent().getPlainText();
        if (oldText !== text) {
            if (maxlength && text.length > maxlength) {
                if (window.confirm("The text to be entered exceeds the maximum allowable input length, further input may be truncated.")) {
                    const newText = text.slice(0, maxlength);
                    const newState = EditorState.createWithContent(ContentState.createFromText(newText));
                    setEditorState(newState)
                }
            } else {
                setEditorState(newEditorState)
            }
        }

        return differ(() => {
            ref?.current?.blur()
        })
    }

    const { MentionSuggestions, plugins } = useMemo(() => {
        const mentionPlugin = createMentionPlugin({
            entityMutability: "IMMUTABLE",
            mentionPrefix: "#",
            mentionTrigger: ["#"],
            mentionComponent: (mentionProps) => {
                return (
                    <div className={`${mentionProps.className}`} onClick={() => {
                        //
                    }}>
                        {mentionProps.children}
                    </div>
                );
            },
        });
        // eslint-disable-next-line no-shadow
        const { MentionSuggestions } = mentionPlugin;
        // eslint-disable-next-line no-shadow
        const plugins = [mentionPlugin];
        return { plugins, MentionSuggestions };
    }, []);

    const customSuggestionsFilter = (searchValue: string, suggestions: any) => {
        const size = (list: any) => (list.constructor.name === "List"
            ? list.size
            : list.length);

        const get = (obj: any, attr: any) => (obj.get
            ? obj.get(attr)
            : obj[attr]);

        const value = searchValue.toLowerCase();
        const filteredSuggestions = suggestions.filter((suggestion: any) => (
            !value || get(suggestion, "name").toLowerCase()
                .indexOf(value) > -1
        ));
        const length = size(filteredSuggestions) < 10 ? size(filteredSuggestions) : 10;
        return filteredSuggestions.slice(0, length);
    };

    const onOpenChange = (isOpen: boolean) => {
        setOpen(isOpen)
    }

    const onSearchChange = (value: string) => {
        setSuggestions(customSuggestionsFilter(value, originalMentions));
    };

    const getCompliedMessage = (editorState: EditorState): string => {
        const rawState = convertToRaw(editorState.getCurrentContent())
        return getCompliedMessageFromRawState(rawState)
    }

    const onChangeEditorState = (newState: EditorState) => {
        const contentState = newState.getCurrentContent();
        if (maxlength) {
            const oldContent = editorState.getCurrentContent();
            const newText = contentState.getPlainText();
            if (contentState === oldContent || newText.length <= maxlength) {
                setEditorState(newState)
                const compliedMessage = getCompliedMessage(newState)
                const libMessage: string = JSON.stringify(convertToRaw(contentState))
                onEditorChange && onEditorChange(libMessage, compliedMessage)
            }
        } else {
            setEditorState(newState)
            const compliedMessage = getCompliedMessage(newState)
            const libMessage: string = JSON.stringify(convertToRaw(contentState))
            onEditorChange && onEditorChange(libMessage, compliedMessage)
        }
    }

    const handleBeforeInput = (chars: string, editorState: EditorState): Draft.DraftModel.Constants.DraftHandleValue => {
        const totalLength = editorState.getCurrentContent().getPlainText().length + chars.length;
        return totalLength > maxlength ? "handled" : "not-handled"
    }

    const handlePastedText = (text: string, html: string | undefined, editorState: EditorState): Draft.DraftModel.Constants.DraftHandleValue => {
        let oldText = editorState.getCurrentContent().getPlainText();
        let newText = "";
        if (maxlength) {
            let restLength = maxlength - oldText.length;
            newText = oldText + text.slice(0, restLength);
        } else {
            newText = oldText + text;
        }

        // Set the new editorState
        setEditorState(
            EditorState.createWithContent(
                ContentState.createFromText(newText)
            )
        );

        // Important! Tell draft-js you already handled the pasted text
        return "handled";
    };

    const onFocus = () => {
        onEditorFocus && onEditorFocus()
    }

    const onBlur = () => {
        const compliedMessage = getCompliedMessage(editorState)
        const libMessage: string = JSON.stringify(convertToRaw(editorState.getCurrentContent()))
        onEditorBlur && onEditorBlur(libMessage, compliedMessage)
    }

    const renderCountLabel = () => {
        let compliedMessage = getCompliedMessage(editorState)
        if (compliedMessage.length) {
            let realMessage = replaceWithReplaceableInfo(compliedMessage, replaceableInfo)
            let count = getSmsMessageLength(realMessage)
            let smsCount = Math.ceil(count / 160.0)
            let maxMsgLength = smsCount * 160
            return (
                <div className={"flex flex-row-reverse mt-2"}>
                    <div className={"px-2 py-1 rounded bg-gray-5"}>
                        <p className={"text-sm"}>{`${count}/${Math.min(maxMsgLength, maxlength)}, ${smsCount} SMS`}</p>
                    </div>
                </div>
            )
        }
        return null
    }

    return (
        <div className={"w-full"}>
            <Editor
                readOnly={readonly}
                editorKey={"editor"}
                editorState={editorState}
                onChange={onChangeEditorState}
                handleBeforeInput={handleBeforeInput}
                handlePastedText={handlePastedText}
                plugins={plugins}
                ref={ref}
                onBlur={onBlur}
                onFocus={onFocus}
            />
            {renderCountLabel()}
            <MentionSuggestions
                open={open}
                onOpenChange={onOpenChange}
                suggestions={suggestions}
                onSearchChange={({value}) => {
                    onSearchChange(value)
                }}
                onAddMention={() => {
                    // get the mention object selected
                }}
                entryComponent={Entry}
            />
        </div>
    );
}
